import { FEATURE_FLAG_KEYS } from './feature-flag.constants';

export interface IMasterFeatureFlag {
    id: string;
    key: string;
    name: string;
    description?: string;
    enabled: boolean; // Master switch - controls everything
}

export interface IMasterFeatureFlagsConfig {
    version: string;
    features: IMasterFeatureFlag[];
}

// Master configuration - PLATFORM-AGNOSTIC features only
export const MASTER_FEATURE_FLAGS: IMasterFeatureFlagsConfig = {
    version: '1.0.0',
    features: [
        {
            id: FEATURE_FLAG_KEYS.PULL_TASK,
            key: FEATURE_FLAG_KEYS.PULL_TASK,
            name: 'Pull task',
            description: 'Enable pull task functionality across the platform',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.PULL_TASK),
        },
        {
            id: FEATURE_FLAG_KEYS.GLOBAL_CUSTOM_CODE,
            key: FEATURE_FLAG_KEYS.GLOBAL_CUSTOM_CODE,
            name: 'Global Custom Code',
            description: 'Enable global custom code functionality across the platform',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.GLOBAL_CUSTOM_CODE),
        },
        {
            id: FEATURE_FLAG_KEYS.PROJECT_PLUGINS,
            key: FEATURE_FLAG_KEYS.PROJECT_PLUGINS,
            name: 'Project Plugins',
            description: 'Enable project plugins functionality across the platform',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.GLOBAL_CUSTOM_CODE),
        },
        {
            id: FEATURE_FLAG_KEYS.PROJECT_SELECTION,
            key: FEATURE_FLAG_KEYS.PROJECT_SELECTION,
            name: 'Project selection',
            description: 'Enable Project selection functionality for custom code',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.GLOBAL_CUSTOM_CODE),
        },
        // Please ensure that page selection is enabled only when project selection is true.
        {
            id: FEATURE_FLAG_KEYS.PAGE_SELECTION,
            key: FEATURE_FLAG_KEYS.PAGE_SELECTION,
            name: 'Page selection',
            description: 'Enable page selection functionality for custom code',
            enabled: false,
        },
        {
            id: FEATURE_FLAG_KEYS.ADMIN_FILTERS,
            key: FEATURE_FLAG_KEYS.ADMIN_FILTERS,
            name: 'Admin filters',
            description: 'Enable admin filters functionality across the platform',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.ADMIN_FILTERS),
        },
        {
            id: FEATURE_FLAG_KEYS.INBOX_PROJECTION,
            key: FEATURE_FLAG_KEYS.INBOX_PROJECTION,
            name: 'Inbox projection',
            description: 'Enable inbox projection functionality across the platform',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.INBOX_PROJECTION),
        },
        {
            id: FEATURE_FLAG_KEYS.CHANGE_TRACKING,
            key: FEATURE_FLAG_KEYS.CHANGE_TRACKING,
            name: 'Change tracking',
            description: 'Enable change tracking and versioning functionality',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.CHANGE_TRACKING),
        },
        {
            id: FEATURE_FLAG_KEYS.BPM_CONFIG_VERSION,
            key: FEATURE_FLAG_KEYS.BPM_CONFIG_VERSION,
            name: 'BPM config versioning',
            description: 'Enable BPM configuration versioning (requires CHANGE_TRACKING)',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.BPM_CONFIG_VERSION),
        },
        {
            id: FEATURE_FLAG_KEYS.FALLBACK_TO_MAIN_CASEDEF,
            key: FEATURE_FLAG_KEYS.FALLBACK_TO_MAIN_CASEDEF,
            name: 'Fallback to main case definition',
            description:
                'When a case definition is not found on the requested branch, fall back to main (requires CHANGE_TRACKING)',
            enabled: isFeatureEnabled(FEATURE_FLAG_KEYS.FALLBACK_TO_MAIN_CASEDEF),
        },
    ],
};

// Common utility functions for both frontend and backend
export class FeatureFlagUtils {
    static isMasterFeatureEnabled(featureKey: string): boolean {
        const feature = MASTER_FEATURE_FLAGS.features.find((f) => f.key === featureKey);
        return feature ? feature.enabled : false;
    }

    static getMasterFeature(featureKey: string): IMasterFeatureFlag | undefined {
        return MASTER_FEATURE_FLAGS.features.find((f) => f.key === featureKey);
    }

    static getAllMasterFeatures(): IMasterFeatureFlag[] {
        return MASTER_FEATURE_FLAGS.features;
    }

    static getMasterConfig(): IMasterFeatureFlagsConfig {
        return MASTER_FEATURE_FLAGS;
    }
}

export function parseFlagValue(raw: any): string[] {
    if (!raw) return [];

    // If already an array, normalize and return
    if (Array.isArray(raw)) {
        return raw.map((v) => String(v).trim()).filter(Boolean);
    }

    // Only try JSON.parse if it's a string
    if (typeof raw === 'string') {
        const s = raw.trim();
        if (!s) return [];

        try {
            const parsed = JSON.parse(s);
            if (Array.isArray(parsed)) {
                return parsed.map((v) => String(v).trim()).filter(Boolean);
            }
            console.warn('FEATURE_FLAGS parsed JSON is not an array. Ignoring.');
            return [];
        } catch (err) {
            console.warn(`FEATURE_FLAGS JSON parsing failed. Expected JSON array. Error: ${err}`);
            return [];
        }
    }

    // For anything else (objects, numbers), ignore — do not coerce to CSV
    console.warn('FEATURE_FLAGS is not an array or JSON array string. Ignoring.');
    return [];
}

export function getFeatureFlagRaw(): string | undefined {
    if ((globalThis as any)?.process?.env?.['FEATURE_FLAGS'])
        return (globalThis as any)?.process?.env?.['FEATURE_FLAGS'];

    if ((globalThis as any)?.window?.environments?.['FEATURE_FLAGS'])
        return (globalThis as any)?.window?.environments['FEATURE_FLAGS'];

    console.warn('FEATURE_FLAGS is not defined anywhere (backend or frontend).');
    return undefined;
}

export function isFeatureEnabled(featureKey: string): boolean {
    const raw = getFeatureFlagRaw();
    const enabledKeys = parseFlagValue(raw);
    return enabledKeys.some((k) => k.toLowerCase() === featureKey.toLowerCase());
}
